import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Modal, Table, message, type TableProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  listAccountComputeChargeScenarios,
  type AccountComputeChargeScenario,
} from '../../api';

interface ComputePowerRulesModalProps {
  open: boolean;
  onClose: () => void;
}

interface ComputeRuleRow {
  key: string;
  taskContent: string;
  price: string;
}

function formatUnitAmount(value?: number | string) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return numeric.toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

function formatUnitName(value?: string | null) {
  if (!value) {
    return '';
  }
  if (value === '秒') {
    return 's';
  }
  return value;
}

function resolveRulePrice(item: AccountComputeChargeScenario) {
  const amount = formatUnitAmount(item.unitAmount);
  if (item.chargeType === 'PER_UNIT') {
    const unitName = formatUnitName(item.unitName);
    return unitName ? `${amount} / ${unitName}` : amount;
  }
  return amount;
}

function buildRows(scenarios: AccountComputeChargeScenario[]): ComputeRuleRow[] {
  const visibleScenarios = scenarios
    .filter((item) => item.enabled !== false)
    .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0));

  return visibleScenarios.map((item) => ({
    key: `${item.scenarioCode}-${item.id}`,
    taskContent: item.scenarioName || item.remark || item.scenarioCode,
    price: resolveRulePrice(item),
  }));
}

export const ComputePowerRulesModal: React.FC<ComputePowerRulesModalProps> = ({
  open,
  onClose,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState<AccountComputeChargeScenario[]>([]);

  const rows = useMemo(() => buildRows(scenarios), [scenarios]);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listAccountComputeChargeScenarios();
      setScenarios(payload || []);
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '算力规则加载失败');
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    if (open) {
      void loadRules();
    }
  }, [loadRules, open]);

  const columns: TableProps<ComputeRuleRow>['columns'] = [
    {
      title: '任务内容',
      dataIndex: 'taskContent',
      width: 459,
    },
    {
      title: (
        <span className="main-layout__compute-rules-price-title">
          算力豆定价
          <small>（个）</small>
        </span>
      ),
      dataIndex: 'price',
      width: 156,
    },
  ];

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        centered
        closable={false}
        footer={null}
        width={695}
        destroyOnHidden
        maskClosable
        onCancel={onClose}
        className="main-layout__compute-rules-modal"
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      >
        <div className="main-layout__compute-rules-shell">
          <header className="main-layout__compute-rules-header">
            <h3>算力规则</h3>
            <Button
              type="text"
              className="main-layout__compute-rules-close"
              icon={<CloseOutlined />}
              aria-label="关闭算力规则弹窗"
              onClick={onClose}
            />
          </header>
          <Table<ComputeRuleRow>
            className="main-layout__compute-rules-table"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={false}
            rowKey="key"
            scroll={{ y: 'calc(min(760px, 100vh - 48px) - 214px)' }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无算力规则" /> }}
          />
        </div>
      </Modal>
    </>
  );
};
